import { useCallback } from "react";

import { ENTRY_DATA_SECTION_KEYS } from "@frontend/common";

import {
  DealLaScDealTermsPartnerAxisTableShellKeys,
  DealLaScDealTermsSingleTableShellKeys,
  DealLaScSection,
  type EntryDataValueAndOptions,
} from "@/types/dealLaSc.types";
import {
  useTableCreatorFocusStatus,
  DEFAULT_FOCUS_STATUS,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import {
  useTableCreatorPositionInfo,
  DEFAULT_POSITION_INFO,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import type { ArticleEntryData } from "@/types/articleEntryData.types";

interface UseHandleTableCreatorShellReturn {
  handleClickToClearSingleTableShell: () => void;
  handleKeyDownToClearSingleTableShell: (event: KeyboardEvent) => void;
}

interface PositionInfoProps {
  groupTableKey: DealLaScSection;
  groupTableIndex: number;
  groupShellKey: string | null;
  groupShellIndex: number;
  tableKey: string | null;
  tableIndex: number;
  shellKey: string;
  shellIndex: number;
}

export const useHandleDealLaScSingleTableShell = (
  positionPropsInfo: PositionInfoProps,
): UseHandleTableCreatorShellReturn => {
  const { changeCurrentHistory } = useTableCreatorCurrentHistory();
  const { info, changeInfo } = useTableCreatorPositionInfo();
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();
  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());

  const handleClearShell = useCallback(() => {
    const singleShellKeyList: DealLaScDealTermsSingleTableShellKeys[] = [
      "Additional Information",
      "Deal Purpose",
      "Other Financial Terms",
    ];
    const clearSingleTableShell = (): ArticleEntryData => {
      const currentSingleTableShell =
        entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc];

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          [info.shellKey as DealLaScDealTermsSingleTableShellKeys]: {
            ...currentSingleTableShell?.[
              info.shellKey as DealLaScDealTermsSingleTableShellKeys
            ],
            value: [],
          },
        },
      };
    };

    const partnerAxisShellKeyList: DealLaScDealTermsPartnerAxisTableShellKeys[] =
      [
        "Partner A Name",
        "Partner A Responsibility & Rights",
        "Partner B Name",
        "Partner B Responsibility & Rights",
        "Partner C Name",
        "Partner C Responsibility & Rights",
      ];
    const clearPartnerAxisShell = (): ArticleEntryData => {
      const currentPartnerAxisShell =
        entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"];
      const emptyValueAndOptions: EntryDataValueAndOptions = {
        value: [],
        options: [],
      };
      const shellKey =
        info.shellKey as DealLaScDealTermsPartnerAxisTableShellKeys;

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Partner Axis": {
            "Partner A Name":
              currentPartnerAxisShell?.["Partner A Name"] ??
              emptyValueAndOptions,
            "Partner A Responsibility & Rights":
              currentPartnerAxisShell?.["Partner A Responsibility & Rights"] ??
              emptyValueAndOptions,
            "Partner B Name":
              currentPartnerAxisShell?.["Partner B Name"] ??
              emptyValueAndOptions,
            "Partner B Responsibility & Rights":
              currentPartnerAxisShell?.["Partner B Responsibility & Rights"] ??
              emptyValueAndOptions,
            "Partner C Name":
              currentPartnerAxisShell?.["Partner C Name"] ??
              emptyValueAndOptions,
            "Partner C Responsibility & Rights":
              currentPartnerAxisShell?.["Partner C Responsibility & Rights"] ??
              emptyValueAndOptions,
            [shellKey]: {
              ...currentPartnerAxisShell?.[shellKey],
              value: [],
            },
          },
        },
      };
    };

    if (
      singleShellKeyList.includes(
        info.shellKey as DealLaScDealTermsSingleTableShellKeys,
      )
    ) {
      setEntryData(clearSingleTableShell());
      changeCurrentHistory({
        entryData: clearSingleTableShell(),
        changedDataInfo: {
          target: "shell",
          action: "clear",
          position: {
            ...positionPropsInfo,
            value: null,
            valueIndex: null,
          },
        },
      });
    }
    if (
      partnerAxisShellKeyList.includes(
        info.shellKey as DealLaScDealTermsPartnerAxisTableShellKeys,
      )
    ) {
      setEntryData(clearPartnerAxisShell());
      changeCurrentHistory({
        entryData: clearPartnerAxisShell(),
        changedDataInfo: {
          target: "shell",
          action: "clear",
          position: {
            ...positionPropsInfo,
            value: null,
            valueIndex: null,
          },
        },
      });
    }

    changeFocusStatus(DEFAULT_FOCUS_STATUS);
    changeInfo(DEFAULT_POSITION_INFO);
  }, [
    changeCurrentHistory,
    changeFocusStatus,
    changeInfo,
    entryData,
    info,
    positionPropsInfo,
    setEntryData,
  ]);

  const handleClickToClearSingleTableShell = () => {
    if (focusStatus.type === "clear") {
      handleClearShell();
    }
  };

  const handleKeyDownToClearSingleTableShell = useCallback(
    (event: KeyboardEvent) => {
      if (focusStatus.type === "clear" && event.key === "Delete") {
        handleClearShell();
      }
    },
    [focusStatus.type, handleClearShell],
  );

  return {
    handleClickToClearSingleTableShell,
    handleKeyDownToClearSingleTableShell,
  };
};
